import { Injectable } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';

export interface N8nChatState {
  isPending: boolean;
  userMessage: string | null;
  timestamp: number | null;
  timeout: number; // in milliseconds
  interactionId?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class N8nChatService {
  private readonly TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly STORAGE_KEY = 'n8n_chat_state';
  private timeoutSubscription: any;

  public chatState: BehaviorSubject<N8nChatState> =
    new BehaviorSubject<N8nChatState>({
      isPending: false,
      userMessage: null,
      timestamp: null,
      timeout: this.TIMEOUT_DURATION,
      interactionId: null,
    });

  constructor() {
    this.loadStateFromStorage();
    this.checkForExpiredRequest();
  }

  public createRequest(userMessage: string, interactionId?: string): void {
    if (this.chatState.value.isPending) {
      throw new Error(
        'A request is already pending. Only one request at a time is allowed.'
      );
    }

    const timestamp = Date.now();

    const newState: N8nChatState = {
      isPending: true,
      userMessage,
      timestamp,
      timeout: this.TIMEOUT_DURATION,
      interactionId: interactionId || null,
    };

    this.chatState.next(newState);
    this.saveStateToStorage(newState);

    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
    }

    this.timeoutSubscription = timer(this.TIMEOUT_DURATION).subscribe(() => {
      this.timeoutRequest();
    });
  }

  public cancelRequest(): void {
    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
    }

    const clearedState: N8nChatState = {
      isPending: false,
      userMessage: null,
      timestamp: null,
      timeout: this.TIMEOUT_DURATION,
      interactionId: null,
    };

    this.chatState.next(clearedState);
    this.saveStateToStorage(clearedState);
  }

  public completeRequest(): void {
    this.cancelRequest();
  }

  public isPending(): boolean {
    return this.chatState.value.isPending;
  }

  public getCurrentRequest(): N8nChatState {
    return this.chatState.value;
  }

  private timeoutRequest(): void {
    console.warn(`N8N request timed out after ${this.TIMEOUT_DURATION}ms`);
    this.cancelRequest();
  }

  private loadStateFromStorage(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const state: N8nChatState = JSON.parse(stored);
        if (
          state.timestamp &&
          Date.now() - state.timestamp < this.TIMEOUT_DURATION
        ) {
          this.chatState.next(state);
          const remainingTime =
            this.TIMEOUT_DURATION - (Date.now() - state.timestamp);
          this.timeoutSubscription = timer(remainingTime).subscribe(() => {
            this.timeoutRequest();
          });
        } else {
          this.clearStoredState();
        }
      } catch (e) {
        console.error('Error loading N8N chat state from storage:', e);
        this.clearStoredState();
      }
    }
  }

  private saveStateToStorage(state: N8nChatState): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private clearStoredState(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  private checkForExpiredRequest(): void {
    const currentState = this.chatState.value;
    if (currentState.isPending && currentState.timestamp) {
      const currentTime = Date.now();
      if (currentTime - currentState.timestamp >= this.TIMEOUT_DURATION) {
        this.cancelRequest();
      }
    }
  }
}
